// The face of an achievement collectible: the medal, title, description, and
// chip on a 16:10 card (a game library card's cover), in the track's level
// color, or neutral once complete. The medal and text sit slightly off the
// face, which shows only under a 3D parent. Children render last, on top.

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/utilities/cn';
import type { AchievementTrackProgress } from '@airhead-games/shared/types';
import AchievementMedal from './AchievementMedal';
import { isOneTime } from './achievementTrack';
import { trackPaletteStyle } from './trackPalette';

export type AchievementCardFaceProps = {
  track: AchievementTrackProgress;
  level: number; // the level shown on the medal and in the color
  complete?: boolean;
  description: React.ReactNode;
  chip: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
};

const AchievementCardFace: React.FC<AchievementCardFaceProps> = ({
  track,
  level,
  complete = false,
  description,
  chip,
  className,
  style,
  children,
}) => (
  <Card
    style={{ ...trackPaletteStyle(track.accent, level), ...style }}
    className={cn(
      'relative aspect-[16/10] w-full items-center justify-center gap-0 overflow-visible p-5 text-center ring-1 ring-foreground/10',
      complete
        ? 'bg-muted text-foreground'
        : 'bg-(--lvl-50) text-(--lvl-950) dark:bg-(--lvl-950) dark:text-(--lvl-50)',
      className
    )}
  >
    <div style={{ transform: 'translateZ(14px)' }}>
      <AchievementMedal
        icon={track.icon}
        level={isOneTime(track) ? null : level}
        complete={complete}
      />
    </div>
    <div
      className="flex flex-col items-center"
      style={{ transform: 'translateZ(8px)' }}
    >
      <div className="mt-3 flex items-center gap-2">
        <h3 className="text-xl font-semibold">{track.title}</h3>
        {track.status === 'archived' && (
          <Badge variant="outline">Retired</Badge>
        )}
      </div>
      <div className="mt-0.5 text-sm opacity-75">{description}</div>
      <div className="mt-3">{chip}</div>
    </div>
    {children}
  </Card>
);

export default AchievementCardFace;
